import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/i18n";

export default function Lightbox({ items, index, onClose, onIndexChange }) {
  const { t, L } = useLang();
  const open = index !== null && index >= 0;

  const step = useCallback(
    (delta) => {
      if (!items.length) return;
      onIndexChange((index + delta + items.length) % items.length);
    },
    [index, items.length, onIndexChange]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, step]);

  if (!open) return null;
  const item = items[index];

  return createPortal(
    <AnimatePresence>
      <motion.div
        data-testid="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] flex flex-col bg-black/97 backdrop-blur-md"
        onClick={onClose}
      >
        <div className="flex items-center justify-between px-5 py-5 text-white/70 sm:px-8">
          <span className="text-xs tracking-[0.25em] tabular-nums">
            {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </span>
          <button
            type="button"
            data-testid="lightbox-close"
            aria-label={t("gallery_close")}
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-white hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative flex flex-1 items-center justify-center px-4 pb-4 sm:px-16">
          <button
            type="button"
            data-testid="lightbox-prev"
            aria-label={t("gallery_prev")}
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            className="absolute start-2 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-white hover:text-white sm:start-5"
          >
            <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
          </button>

          <motion.img
            key={item.image}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            src={item.image}
            alt={L(item.caption)}
            data-testid="lightbox-image"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[78vh] max-w-full object-contain"
          />

          <button
            type="button"
            data-testid="lightbox-next"
            aria-label={t("gallery_next")}
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            className="absolute end-2 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-white hover:text-white sm:end-5"
          >
            <ChevronRight className="h-5 w-5 rtl:rotate-180" />
          </button>
        </div>

        {L(item.caption) && (
          <p data-testid="lightbox-caption" className="px-5 pb-8 text-center text-sm text-white/70 sm:px-8">
            {L(item.caption)}
          </p>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
